import React from 'react'
import { Redirect, Route, RouteProps } from 'react-router-dom'

import { UserPermissionsState } from '../../redux/reducers/initialState'

import { usePermissions } from './permission/usePermissions'
import Spinner from './Spinner'

interface IProtectedRouteProps extends RouteProps {
  component: React.ComponentType<any>
  requiredPermissions?: (keyof UserPermissionsState)[]
  isHomeComponent?: boolean
}

/**
 * ProtectedRoute Component
 *
 * This component is used to protect routes that require user authentication and specific permissions.
 * It checks if the user is logged in and has the necessary permissions to access the route.
 * If the user is not logged in, it displays a sign-in prompt.
 * If the user is logged in but does not have the required permissions, it redirects to an unauthorized page.
 * If the user is logged in and has the required permissions, it renders the specified component.
 *
 * @param {ProtectedRouteProps} props - The props for the ProtectedRoute component.
 * @returns {JSX.Element} The rendered ProtectedRoute component.
 */
export const ProtectedRoute: React.FC<IProtectedRouteProps> = ({
  component: Component,
  requiredPermissions,
  isHomeComponent = false,
  ...rest
}) => {
  const { isLoginCompleted, userPermissionsState } = usePermissions()

  /**
   * Checks if the user has the required permissions to access the route.
   *
   * @returns {boolean} True if the user has the required permissions, false otherwise.
   */
  const hasUserPermissionsAccess = (): boolean => {
    if (!requiredPermissions || !isLoginCompleted) {
      return true
    }

    return (
      userPermissionsState !== null &&
      requiredPermissions.some(
        (permission) => userPermissionsState[permission] === true,
      )
    )
  }

  /**
   * Renders the appropriate component or redirects based on user permissions.
   *
   * @returns {JSX.Element} The component to render or a redirect to another route.
   *
   * The function checks if the user has any of the required permissions. If so, it renders the specified component.
   * If the user has access to Entra ID roles, it redirects to the "/entraidroles" route.
   * Otherwise, it redirects to the "/unauthorized" route.
   */
  const renderHome = () => {
    if (
      userPermissionsState &&
      requiredPermissions &&
      requiredPermissions.some(
        (permission) => userPermissionsState[permission] === true,
      )
    ) {
      return <Component />
    } else if (
      userPermissionsState &&
      userPermissionsState.hasEntraIDRolesAccess
    ) {
      return <Redirect to="/entraidroles" />
    } else {
      return <Redirect to="/unauthorized" />
    }
  }

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!isLoginCompleted) {
          return <></>
        } else if (hasUserPermissionsAccess()) {
          return <Component {...props} />
        } else if (isHomeComponent) {
          return renderHome()
        } else {
          return (
            <Redirect
              to={{
                pathname: '/unauthorized',
                state: { from: props.location },
              }}
            />
          )
        }
      }}
    />
  )
}
